import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { AlertController } from '@ionic/angular';
import { addDays } from 'date-fns';
import { Subscription } from 'rxjs';
import { Avtaler } from 'src/app/services/avtale/avtale.types';
import { Anlegg } from 'src/app/services/forbruk/forbruk.types';
import { StromstotteService } from 'src/app/services/stromstotte/stromstotte.service';
import { sumNum } from 'src/app/utils/array-utils';
import { hasChanged } from 'src/app/utils/change-utils';
import { getDaysInMonth } from 'src/app/utils/date-utils';

@Component({
  selector: 'app-nokkeltall-hour',
  templateUrl: './hour.component.html',
  styleUrls: ['./hour.component.scss']
})
export class NokkeltallHourComponent implements OnInit, OnChanges, OnDestroy {
  @Input() valgtTimeAnlegg: Anlegg;
  @Input() selectedDate: Date;
  @Input() valgtAvtale: Avtaler;
  @Input() foretakMap: Map<number, string>;
  @Input() isStromstotteToggled: boolean;

  currentDate: Date;
  stromFastbelop: number;
  nettleieFastbelop: number;
  isPlusskunde: boolean;
  totaltForbruk: number;
  totaltBelop: number;
  stromstotte: number = 0;
  inntekt: number;
  produksjon: number;
  showLoader: boolean = true;
  subscription: Subscription;

  constructor(
    private _alertController: AlertController,
    private _stromstotte: StromstotteService,
    private _changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.currentDate =
      new Date().getDate() === 1 ? addDays(new Date(), -1) : new Date();
    this.getCalculations();
    // this.calcHourValues();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      !(
        changes.valgtTimeAnlegg?.firstChange ||
        changes.selectedDate?.firstChange
      ) &&
      this.valgtTimeAnlegg &&
      this.selectedDate
    ) {
      if (
        hasChanged([
          changes.valgtTimeAnlegg,
          changes.selectedDate,
          changes.isStromstotteToggled
        ])
      ) {
        this.getCalculations();
      }
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  getCalculations() {
    if (
      this.isStromstotteToggled &&
      new Date(2023, 8, 1) <= this.selectedDate
    ) {
      this.subscription = this._stromstotte
        .stromstotteResultForDay$(
          this.valgtTimeAnlegg.objectid,
          this.selectedDate
        )
        .subscribe((res) => {
          this.stromstotte = res?.receivesTotal || 0;
          this.showLoader = false;
          this.calcHourValues();
          this._changeDetectorRef.detectChanges();
        });
    } else {
      this.showLoader = false;
      this.calcHourValues();
      this._changeDetectorRef.detectChanges();
    }
  }

  calcHourValues() {
    const noOfDays = getDaysInMonth(this.selectedDate);
    this.isPlusskunde = this.valgtTimeAnlegg.plusskunde;
    const hasGjennomfakturering = this.valgtTimeAnlegg.gjennomfakturering;
    this.stromFastbelop =
      this.valgtTimeAnlegg.priser?.fastbelopMnd / noOfDays || 0;
    const stromForbrukbelop =
      this.valgtTimeAnlegg.verdiList
        ?.map((x) => x.belopKraft)
        .reduce((a, b) => a + b, 0) || 0;
    const stromTotal = this.stromFastbelop + stromForbrukbelop || 0;

    this.nettleieFastbelop =
      this.valgtTimeAnlegg.priser?.nettleiepriser?.fastbelopMnd / noOfDays || 0;
    const nettleieEnergiledd =
      this.valgtTimeAnlegg.priser?.nettleiepriser?.energi || 0;
    const nettleieAvgifter =
      this.valgtTimeAnlegg.priser?.nettleiepriser?.forbruksavgift || 0;
    const nettleieTotal = hasGjennomfakturering
      ? nettleieAvgifter + nettleieEnergiledd + this.nettleieFastbelop
      : 0;

    if (this.isPlusskunde) {
      this.totaltForbruk =
        this.valgtTimeAnlegg.priser?.forbrukKWh -
        this.valgtTimeAnlegg.priser?.produksjonTotaltKWh;
      this.totaltBelop =
        stromTotal + nettleieTotal + this.valgtTimeAnlegg.priser?.plussTotalt;
      this.inntekt =
        sumNum(
          this.valgtTimeAnlegg.verdiList?.map((x) => -x.belopPlussverdi)
        ) || 0;
      this.produksjon =
        sumNum(this.valgtTimeAnlegg.verdiList?.map((x) => x.plussverdi)) || 0;
    } else {
      this.totaltBelop = stromTotal + nettleieTotal - this.stromstotte;
      this.totaltForbruk = this.valgtTimeAnlegg.priser?.forbrukKWh || 0;
    }
  }

  toRoundNumber(num: number) {
    return num.toFixed(2).replace('.', ',');
  }

  async expandTooltip() {
    const message = `<p>Estimert kostnad er basert på pris tilknyttet forbruk${this.isStromstotteToggled ? ', minus estimert strømstøtte per time' : ''
      }. Fastbeløp er også inkludert for valgt dag.</p>
      <p> For ${this.selectedDate.toLocaleDateString('nb-NO', {
        day: 'numeric',
        month: 'long'
      })} er fastbeløpet for strøm <strong>${this.toRoundNumber(
        this.stromFastbelop
      )}</strong> kr${this.valgtTimeAnlegg.gjennomfakturering
        ? ` og fastbeløpet for nettleie <strong>${this.toRoundNumber(
          this.nettleieFastbelop
        )}</strong> kr`
        : ''
      }.${this.isStromstotteToggled
        ? ` Strømstøtten denne dagen er beregnet til <strong>${this.toRoundNumber(
          this.stromstotte
        )}</strong> kr.`
        : ''
      } Sammen med forbruk og avgifter blir summen <strong>${this.toRoundNumber(
        this.totaltBelop
      )}</strong> kr.</p>
    <p> Vi tar forbehold om feil. Endelig kostnad vil fremkomme på din strømregning.</p> `;

    const initalert = await this._alertController.create({
      cssClass: 'push-alert',
      mode: 'ios',
      header: 'Foreløpig estimert kostnad',
      message,
      buttons: ['Lukk']
    });
    await initalert.present();
  }
}
